import { useCallback } from "react";
import { Image, TouchableOpacity, View } from "react-native";

import { MapPin } from "@smartrent/icons";
import { StatusBadge, Typography, useTheme } from "@smartrent/ui";

import { useDialog } from "@/context/dialog";
import { NearbyCommunityType } from "@/types";
import { formatNearbyCommunityDetails, getAddress } from "@/utils/community";

interface NearbyCommunityCardProps {
  community: NearbyCommunityType;
  onPress: VoidFunction;
  horizontal?: boolean;
}

export function NearbyCommunityCard({
  community,
  onPress,
  horizontal = false,
}: NearbyCommunityCardProps) {
  const { colors } = useTheme();
  const { confirm } = useDialog();

  const confirmation = useCallback(async () => {
    const confirmed = await confirm({
      title: "Leaving Website",
      description: "You are about to be redirected to a different website.",
      confirmText: "Continue",
      confirmType: "primary",
    });

    if (confirmed) {
      window.open(
        `https://maps.google.com/maps?q=${community.property.lat},${community.property.lng}`,
        "_blank"
      );
    }
  }, [community.property.lat, community.property.lng, confirm]);

  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.8}
      style={{
        marginRight: horizontal ? 16 : 0,
        width: horizontal ? "83vw" : "100%",
      }}
    >
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          paddingVertical: 16,
        }}
      >
        <Typography type="title4" numberOfLines={1} color="primary">
          {community.propertyName}
        </Typography>
        <StatusBadge legacySize="title4">{`${community.distance} miles`}</StatusBadge>
      </View>
      <View style={{ borderRadius: 4, overflow: "hidden" }}>
        <Image
          source={{ uri: community.propertyImageUrl }}
          resizeMode="cover"
          style={{
            height: 150,
            width: "100%",
          }}
        />
      </View>
      <View style={{ paddingVertical: 8 }}>
        <Typography type="body" numberOfLines={1}>
          {formatNearbyCommunityDetails(community)}
        </Typography>
        <TouchableOpacity
          onPress={() => confirmation()}
          activeOpacity={0.8}
          style={{ flexDirection: "row", paddingVertical: 8 }}
        >
          <MapPin color={colors.primary} size={18} style={{ marginRight: 4 }} />
          <Typography type="caption" numberOfLines={1} color="primary">
            {getAddress(community.property)}
          </Typography>
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );
}
